import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@heyroot/ui/primitives/Button';
import { Input } from '@heyroot/ui/primitives/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heyroot/backoffice-ui';
import { createTenant, fetchBusinessTypes, queryKeys, AdminApiError } from '../lib/api';

// Copies business-type-new.tsx's idiom exactly (Task 12 step 1): plain
// controlled state, slugify(displayName) auto-fill until manually edited,
// submit disabled until valid, loading on the button.

// Server bound: tenants_slug_format_check (modules/tenants/schema/index.ts:1904)
// -- "slug = LOWER(slug) AND slug ~ '^[a-z0-9][a-z0-9_-]*$'". Digit-leading is
// legal here (unlike business-type-new.tsx's letter-leading rule) -- do not
// copy that pattern verbatim, this is a DIFFERENT check constraint.
const SLUG_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;
const SLUG_MAX = 64;

function slugProblem(value: string): string | null {
  if (value.length === 0) return null;
  if (!SLUG_PATTERN.test(value)) {
    return 'Slug must start with a lowercase letter or digit and use only lowercase letters, digits, underscores and hyphens.';
  }
  if (value.length > SLUG_MAX) return `Slug must be ${SLUG_MAX} characters or fewer.`;
  return null;
}

function slugify(name: string): string {
  const base = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return base.slice(0, SLUG_MAX);
}

function isValidEmail(v: string): boolean {
  return /.+@.+\..+/.test(v);
}

export function TenantNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [displayName, setDisplayName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [businessTypeSlug, setBusinessTypeSlug] = useState('');
  const [timezone, setTimezone] = useState('');
  const [slugError, setSlugError] = useState<string | null>(null);
  const [businessTypeError, setBusinessTypeError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Business-type select lists only PUBLISHED types (step 4) -- a
  // draft-only type would 409 BUSINESS_TYPE_NOT_PUBLISHED server-side.
  const { data: businessTypesData } = useQuery({
    queryKey: queryKeys.businessTypes({ isActive: true }),
    queryFn: () => fetchBusinessTypes({ isActive: true }),
  });
  const publishedTypes = (businessTypesData?.rows ?? []).filter(
    (t) => t.publishedVersionNumber !== null,
  );

  const create = useMutation({
    mutationFn: () =>
      createTenant({
        displayName,
        ownerEmail,
        // Trap #2 (batch-C brief): z.string().min(1).optional() REJECTS ''.
        // Every optional field below is OMITTED when blank, never sent empty.
        ...(slug ? { slug } : {}),
        ...(businessTypeSlug ? { businessTypeSlug } : {}),
        ...(timezone ? { timezone } : {}),
      }),
    onSuccess: async (result) => {
      setFormError(null);
      setSlugError(null);
      setBusinessTypeError(null);
      // Trap #6/spec §2.14: the tenants list is OFFSET-paginated, not cursor
      // -- invalidate the whole ['tenants', ...] family (every param variant),
      // not a single cursor-shaped key.
      await qc.invalidateQueries({ queryKey: ['tenants'] });
      void navigate({
        to: '/tenants/$tenantId',
        params: { tenantId: result.data.tenantId },
      });
    },
    onError: (err) => {
      if (err instanceof AdminApiError) {
        if (err.code === 'TENANT_SLUG_TAKEN') {
          setSlugError('That slug is already in use.');
          return;
        }
        if (err.code === 'BUSINESS_TYPE_NOT_PUBLISHED') {
          // Fix round 2 (I3): the brief's binding line maps this to its field,
          // not the banner -- the create form only lists published types, so
          // this is a race (a type was un-published between load and submit),
          // but the doc wins by default (plan-doc-canonical.md).
          setBusinessTypeError(
            'That business type no longer has a published version. Pick another, or none.',
          );
          return;
        }
        setFormError(err.message);
        return;
      }
      setFormError('Could not create this tenant.');
    },
  });

  const valid =
    displayName.trim().length > 0 &&
    isValidEmail(ownerEmail) &&
    (slug.length === 0 || slugProblem(slug) === null);

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/tenants"
        className="inline-flex items-center gap-1 text-body-sm text-secondary hover:text-primary"
      >
        <ChevronLeft size={14} /> Tenants
      </Link>

      <div>
        <h1 className="text-heading-2 text-primary">New tenant</h1>
        <p className="mt-1 text-body-sm text-muted">
          Provisions the tenant and emails the owner an invite to claim it -- no owner exists until
          they accept.
        </p>
      </div>

      <form
        className="flex max-w-xl flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        {formError && (
          <div
            role="alert"
            className="rounded-control border border-danger bg-danger-pale text-danger-text text-body-sm p-3"
          >
            {formError}
          </div>
        )}

        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm-emphasis text-secondary">Display name</span>
          <Input
            required
            value={displayName}
            onChange={(e) => {
              const v = e.target.value;
              setDisplayName(v);
              if (!slugEdited) setSlug(slugify(v));
            }}
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm-emphasis text-secondary">Slug</span>
          <Input
            value={slug}
            invalid={Boolean(slugError) || slugProblem(slug) !== null}
            onChange={(e) => {
              setSlugEdited(true);
              setSlug(e.target.value);
              setSlugError(null);
            }}
          />
          {slugError ? (
            <span className="text-body-sm text-danger-text">{slugError}</span>
          ) : slugProblem(slug) !== null ? (
            <span className="text-body-sm text-danger-text">{slugProblem(slug)}</span>
          ) : (
            <span className="text-body-sm text-muted">Leave blank to derive it from the name.</span>
          )}
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm-emphasis text-secondary">Owner email</span>
          <Input
            required
            type="email"
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
          />
          <span className="text-body-sm text-muted">
            Sent an invite to claim ownership. No owner exists until they accept.
          </span>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm-emphasis text-secondary">Business type</span>
          <Select
            value={businessTypeSlug}
            onValueChange={(v) => {
              setBusinessTypeSlug(v === '__mixed__' ? '' : v);
              setBusinessTypeError(null);
            }}
          >
            <SelectTrigger aria-label="Business type">
              <SelectValue placeholder="Mixed (default)" />
            </SelectTrigger>
            <SelectContent>
              {/* Fix round 2 (I4): an explicit item back to '' -- without this,
                  once an operator picks a real type there is no way to return
                  to the default short of reloading the page. */}
              <SelectItem value="__mixed__">Mixed (default)</SelectItem>
              {publishedTypes.map((t) => (
                <SelectItem key={t.slug} value={t.slug}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {businessTypeError ? (
            <span className="text-body-sm text-danger-text">{businessTypeError}</span>
          ) : (
            <span className="text-body-sm text-muted">
              Only types with a published version are offerable.
            </span>
          )}
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm-emphasis text-secondary">Timezone</span>
          <Input
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="UTC (default)"
          />
        </label>

        <div className="mt-2 flex gap-2">
          <Button type="submit" loading={create.isPending} disabled={!valid || create.isPending}>
            Create tenant
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void navigate({ to: '/tenants' })}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
